import type { ReactElement } from 'react';
import { Link, NavLink } from 'react-router';

import { useSession } from '@/entities/session';
import { useMediaQuery } from '@/shared/lib/use-media-query';
import { Container } from '@/shared/ui/container';
import { Wordmark } from '@/shared/ui/logo';

import { MemberMenu } from './member-menu';

/**
 * GNB — 1차 오픈 범위(14 §3.1 O-01).
 *
 * 클래스·스토어·번들·케이스·카트를 뺐다. 1차 오픈에는 팔 상품이 없고, 메뉴가 살아 있으면
 * 방문자가 빈 화면에 도착한다. **삭제가 아니라 메뉴에서만 뺀 것**이라
 * 2차 오픈은 여기 배열에 항목을 되돌리면 된다.
 *
 * 검색도 뺐다 — 자료가 2~3개인 시점의 검색은 「결과 없음」을 만드는 장치다.
 *
 * 「교육 문의」는 메뉴가 아니라 로그인 옆의 **민트 버튼**이다. 별도 랜딩으로 붙을 화면이라
 * 탐색 메뉴와 성격이 다르고, 1차 오픈에서 리드가 들어오는 두 번째 문이라 눈에 띄어야 한다.
 *
 * **좁은 화면(640px 미만)은 메뉴를 두 번째 줄로 내린다.** 320px 에서 로고·문의·로그인이 폭을 다 써
 * 메뉴 자리가 0px 이 됐다(375px 도 36px 라 「자료실」이 잘렸다). 두 번째 줄은 가로로 넘기는
 * 탭이라 커뮤니티 등 메뉴가 늘어도 그대로 받는다 — 6개를 넘으면 그때 햄버거를 검토한다.
 * 붙어 다니는 건 윗줄(header)뿐이다. 메뉴 줄까지 붙이면 폰 화면의 12% 를 계속 가린다.
 */
const NAV = [{ label: '자료실', to: '/library' }] as const;

/** Tailwind `sm` 과 같은 선 */
const WIDE_QUERY = '(min-width: 40rem)';

export function SiteHeader(): ReactElement {
  const member = useSession((state) => state.member);
  // jsdom 은 넓은 화면으로 본다 — 기존 화면 테스트가 메뉴를 윗줄에서 찾는다
  const isWide = useMediaQuery(WIDE_QUERY, true);

  return (
    <>
      <header className="sticky top-0 z-20 border-border border-b bg-background">
        <Container className="flex h-16 items-center gap-3 sm:gap-8">
          <Link to="/" aria-label="오톱 홈" className="shrink-0">
            <Wordmark className="w-[92px] text-foreground sm:w-[108px] [--autop-logo-accent:var(--primary)]" />
          </Link>

          {/* 좁은 화면은 빈 칸을 끼우지 않는다 — 간격이 둘로 늘어 320px 에서 로그인 버튼이 오른쪽 여백을 먹는다 */}
          {isWide ? <MainNav className="min-w-0 flex-1" /> : null}

          <HeaderActions isSignedIn={member !== null} />
        </Container>
      </header>
      {isWide ? null : (
        <Container className="border-border border-b bg-background">
          <MainNav className="flex h-11 items-center" />
        </Container>
      )}
    </>
  );
}

function MainNav({ className }: { className: string }): ReactElement {
  return (
    <nav aria-label="주요 메뉴" className={className}>
      <ul className="flex items-center gap-5 overflow-x-auto md:gap-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {NAV.map((item) => (
          <li key={item.to} className="shrink-0">
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`
              }
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function HeaderActions({ isSignedIn }: { isSignedIn: boolean }): ReactElement {
  return (
    <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-4">
      {/* 민트는 글자로 쓸 수 없다(흰 배경 1.29:1) — 테두리로 두르고 글자는 잉크로 둔다.
              호버에서 면으로 뒤집어 눌리는 느낌을 준다 */}
      <Link
        to="/education"
        className="inline-flex h-9 items-center whitespace-nowrap rounded-md border border-primary px-3 text-[13.5px] sm:px-4 font-semibold text-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
      >
        교육·도입 문의
      </Link>
      {isSignedIn ? (
        <MemberMenu />
      ) : (
        <Link
          to="/login"
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:border-foreground"
        >
          로그인
        </Link>
      )}
    </div>
  );
}
